import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { injectModalRef, ModalService } from '../../../../shared/modal';
import { NotificationService, SettingsService } from '../../../../core/services';
import { apiErrorMessage } from '../../../../core/utils/api-error-message';

const MIN_PASSWORD_LENGTH = 8;

const passwordsMatch: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const { password, passwordConfirmation } = (group as ChangePasswordForm).controls;
  return password.value && passwordConfirmation.value && password.value !== passwordConfirmation.value
    ? { passwordMismatch: true }
    : null;
};

type ChangePasswordForm = FormGroup<{
  currentPassword: FormControl<string>;
  password: FormControl<string>;
  passwordConfirmation: FormControl<string>;
}>;

@Component({
  selector: 'app-change-password-modal',
  standalone: false,
  templateUrl: './change-password-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordModal {
  readonly modalRef = injectModalRef<boolean>();

  private readonly settingsService = inject(SettingsService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly minLength = MIN_PASSWORD_LENGTH;

  protected readonly form: ChangePasswordForm = new FormGroup({
    currentPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)] }),
    passwordConfirmation: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  }, { validators: passwordsMatch });

  protected readonly saving = signal(false);
  protected readonly serverError = signal<string | null>(null);

  constructor() {
    this.modalRef.setCanClose(({ reason }) => {
      if (reason === 'close' || !this.form.dirty) return true;
      return this.modalService.confirm({
        title: 'Discard changes?',
        message: "The password you've typed won't be saved.",
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        danger: true,
      });
    });
  }

  protected showError(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  protected submit(): void {
    if (this.saving()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    this.serverError.set(null);
    this.modalRef.setDisableClose(true);

    this.settingsService.changePassword(this.form.getRawValue())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving.set(false);
          this.modalRef.setDisableClose(false);
        }),
      )
      .subscribe({
        next: result => {
          this.notificationService.success(result.message || 'Password updated successfully');
          this.modalRef.close(true);
        },
        error: error => this.serverError.set(apiErrorMessage(error, 'An error occurred while updating your password. Please try again later.')),
      });
  }
}
